import React, { useEffect, useRef, useState } from 'react';

interface Props {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** @deprecated Sin efecto: el campo es de entrada directa, no tiene spinner. */
  step?: number;
  disabled?: boolean;
  /** Ancho y tipografía del input; el resto del estilo lo pone la variante. */
  className?: string;
  /** `celeste` marca los campos editables, como en el tablero original. */
  variant?: 'celeste' | 'plain';
  align?: 'left' | 'right';
  /** Símbolo fijo a la izquierda del número, ej. "$". */
  prefix?: string;
  /** Símbolo fijo a la derecha del número, ej. "%". */
  suffix?: string;
  /**
   * Formato moneda argentina al perder el foco: punto para los miles, coma
   * para los decimales y dos decimales fijos (45000 → "45.000,00").
   */
  currency?: boolean;
  ariaLabel?: string;
  id?: string;
}

/** Solo dígitos, separador decimal y signo: bloquea letras al tipear. */
const NUMERIC_INTERMEDIO = /^-?[\d.,]*$/;

/** Redondea a 2 decimales para no arrastrar la basura del punto flotante. */
function roundForDisplay(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Cómo se ve el número cuando el campo NO tiene foco. */
function toDisplay(value: number, currency: boolean): string {
  if (!Number.isFinite(value)) return '';
  if (currency) {
    return value.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return String(roundForDisplay(value));
}

/**
 * Cómo se ve el número mientras SÍ se edita: plano, sin separadores de miles,
 * con coma decimal en modo moneda. Así se puede tipear de corrido sin que los
 * puntos de miles muevan el cursor.
 */
function toEditable(value: number, currency: boolean): string {
  if (!Number.isFinite(value)) return '';
  const s = String(roundForDisplay(value));
  return currency ? s.replace('.', ',') : s;
}

/** Interpreta lo tipeado. En moneda: los puntos son miles, la coma es decimal. */
function parse(text: string, currency: boolean): number {
  const limpio = currency
    ? text.replace(/\./g, '').replace(',', '.')
    : text.replace(',', '.');
  return Number(limpio);
}

/**
 * Campo numérico de entrada directa.
 *
 * Es `type="text"` a propósito: `type="number"` agrega las flechitas de
 * incrementar/decrementar, que acá no queremos. Con `inputMode="decimal"` el
 * teclado del celular sigue mostrando el pad numérico.
 *
 * Guarda el texto crudo mientras el campo tiene foco, así se puede escribir
 * "22244,8" o borrar todo sin que el valor numérico de arriba lo pise en cada
 * tecla. Al salir del campo normaliza, recorta al rango y —si es moneda— lo
 * muestra formateado a la argentina.
 *
 * El `$` / `%` viajan como `prefix` / `suffix` dentro de la misma caja, para
 * que se lean como parte del valor y no como una etiqueta suelta.
 */
const NumberInput: React.FC<Props> = ({
  value,
  onChange,
  min,
  max,
  disabled = false,
  className = '',
  variant = 'celeste',
  align = 'right',
  prefix,
  suffix,
  currency = false,
  ariaLabel,
  id,
}) => {
  const [text, setText] = useState(() => toDisplay(value, currency));
  const focused = useRef(false);

  // Mientras el campo está en foco manda lo que el usuario tipea; cuando el
  // valor cambia desde afuera (cargar escenario, aplicar aumento) se resincroniza
  // con el formato de reposo.
  useEffect(() => {
    if (focused.current) return;
    setText(toDisplay(value, currency));
  }, [value, currency]);

  const clamp = (n: number): number => {
    let out = n;
    if (min !== undefined) out = Math.max(min, out);
    if (max !== undefined) out = Math.min(max, out);
    return out;
  };

  const handleChange = (raw: string) => {
    if (!NUMERIC_INTERMEDIO.test(raw)) return; // rechaza letras y símbolos
    setText(raw);
    if (raw.trim() === '' || raw === '-' || raw === ',') return; // estado intermedio
    const parsed = parse(raw, currency);
    if (Number.isFinite(parsed)) onChange(clamp(parsed));
  };

  const handleFocus = () => {
    focused.current = true;
    // Pasar a la forma plana para editar sin pelear con los separadores.
    setText(toEditable(value, currency));
  };

  const handleBlur = () => {
    focused.current = false;
    const parsed = parse(text, currency);
    const next = Number.isFinite(parsed) ? clamp(parsed) : (min ?? 0);
    setText(toDisplay(next, currency));
    onChange(next);
  };

  const caja =
    variant === 'celeste'
      ? 'bg-sky-50 border border-sky-500 text-sky-900 focus-within:ring-2 focus-within:ring-sky-300 focus-within:border-sky-600'
      : 'bg-white border border-gray-300 text-gray-800 focus-within:ring-2 focus-within:ring-gappex-primary/30 focus-within:border-gappex-primary';

  const simbolo = 'text-sm font-semibold select-none pointer-events-none opacity-70';

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 transition-colors
        ${disabled ? 'bg-gray-100 border border-gray-200 text-gray-400' : caja} ${className}`}
    >
      {prefix && <span className={simbolo}>{prefix}</span>}
      <input
        id={id}
        type="text"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={text}
        disabled={disabled}
        onFocus={handleFocus}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        className={`min-w-0 flex-1 bg-transparent border-none p-0 text-sm font-semibold outline-none
          disabled:text-gray-400 ${align === 'right' ? 'text-right' : 'text-left'}`}
      />
      {suffix && <span className={simbolo}>{suffix}</span>}
    </div>
  );
};

export default NumberInput;
